'use client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogContent as UntypedDialogContent,
  DialogDescription as UntypedDialogDescription,
  DialogTitle as UntypedDialogTitle,
} from '@/components/ui/dialog'
import { formatDurationCountdown } from '@/lib/countdown'
import {
  serverInfoPreset,
  type ServerInfoFields,
} from '@/lib/event-server-info'
import { Clock, Copy, ExternalLink, Eye, EyeOff, Server } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import useSWR from 'swr'

// The shared JSX primitives do not expose their prop types to TypeScript.
const DialogContent = UntypedDialogContent as React.ComponentType<
  React.PropsWithChildren<{
    className?: string
    onCloseAutoFocus?: (event: Event) => void
  }>
>
const DialogTitle = UntypedDialogTitle as React.ComponentType<
  React.PropsWithChildren<{ className?: string }>
>
const DialogDescription = UntypedDialogDescription as React.ComponentType<
  React.PropsWithChildren<{ className?: string }>
>

type Info = {
  state: 'hidden' | 'missing' | 'scheduled' | 'available'
  opensAt?: string | null
  timezone?: string | null
  fields?: ServerInfoFields
  gameName?: string
  eventName?: string | null
}
async function load(url: string): Promise<Info> {
  const response = await fetch(url, { cache: 'no-store' })
  if (response.status === 401 || response.status === 403)
    return { state: 'hidden' }
  if (!response.ok) throw new Error('Could not load server information')
  return response.json()
}
export default function ServerInfoButton({
  eventId,
  userId,
  attendanceVersion,
}: {
  eventId: string
  userId?: string
  attendanceVersion?: unknown
}) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const url = `/api/v4/events/${eventId}/server-info`
  const { data, error, mutate } = useSWR(
    userId ? [url, userId] : null,
    ([endpoint]) => load(endpoint),
    { refreshInterval: 30000, keepPreviousData: false },
  )
  const [now, setNow] = useState(Date.now)
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState<Info | null>(null)
  const [detailsError, setDetailsError] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  useEffect(() => {
    void mutate()
    setDetails(null)
    setOpen(false)
  }, [attendanceVersion, eventId, userId, mutate])
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('serverInfo') === '1')
      setRequestOpen(true)
  }, [eventId])
  useEffect(() => {
    if (data?.state !== 'scheduled' || !data.opensAt) return
    const timer = window.setInterval(() => {
      const time = Date.now()
      setNow(time)
      if (time >= new Date(data.opensAt!).getTime()) {
        void mutate()
        window.clearInterval(timer)
      }
    }, 1000)
    return () => window.clearInterval(timer)
  }, [data?.state, data?.opensAt, mutate])
  useEffect(() => {
    if (data?.state === 'available' && requestOpen) {
      setOpen(true)
      setRequestOpen(false)
    }
  }, [data?.state, requestOpen])
  useEffect(() => {
    if (data?.state !== 'available' || error) {
      setOpen(false)
      setDetails(null)
      setRevealed(false)
    }
  }, [data?.state, error])
  useEffect(() => {
    if (!open) {
      setDetails(null)
      setRevealed(false)
      return
    }
    let active = true
    setDetailsError(false)
    load(`${url}?details=1`)
      .then((info) => {
        if (!active) return
        if (info.state !== 'available') {
          setOpen(false)
          void mutate()
          return
        }
        setDetails(info)
      })
      .catch(() => {
        if (active) setDetailsError(true)
      })
    return () => {
      active = false
    }
  }, [open, url, mutate])
  if (!userId || (!error && (!data || data.state === 'hidden'))) return null
  if (error)
    return (
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        className="mt-2 w-full"
        onClick={() => void mutate()}
      >
        Retry server information
      </Button>
    )
  const available = data?.state === 'available'
  const scheduled = data?.state === 'scheduled'
  const preset = serverInfoPreset(details?.gameName ?? '')
  const rows: Array<[keyof ServerInfoFields, string]> = [
    ['name', preset.nameLabel],
    ['lobbyId', 'Lobby / session ID'],
    ['address', 'Server address'],
    ['password', 'Password'],
  ]
  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        disabled={!available}
        onClick={() => setOpen(true)}
        className="mt-2 h-auto min-h-11 w-full whitespace-normal rounded-md border-neutral-blue-500 bg-neutral-blue-800 px-3 py-3 text-shades-0 disabled:opacity-70"
      >
        {scheduled ? (
          <Clock className="mr-2 size-4 shrink-0" />
        ) : (
          <Server className="mr-2 size-4 shrink-0" />
        )}
        {available
          ? 'View Joining Info'
          : scheduled
            ? data?.opensAt
              ? `Server info opens in ${formatDurationCountdown(new Date(data.opensAt).getTime() - now)}`
              : 'Server info opening time to be confirmed'
            : 'No server information'}
      </Button>
      {scheduled && data?.opensAt && (
        <p className="mt-2 text-xs text-neutral-blue-300">
          Opens{' '}
          {new Intl.DateTimeFormat('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: data.timezone || 'UTC',
          }).format(new Date(data.opensAt))}{' '}
          ({data.timezone || 'UTC'})
        </p>
      )}
      <Dialog open={open && available} onOpenChange={setOpen}>
        <DialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            triggerRef.current?.focus()
          }}
          className="max-h-[85dvh] w-[calc(100vw-2rem)] gap-6 overflow-y-auto rounded-lg border-neutral-blue-600 bg-neutral-blue-800 p-5 text-shades-0 dark:border-neutral-blue-600 dark:bg-neutral-blue-800 sm:max-w-[600px] sm:p-7"
        >
          <DialogHeader className="space-y-3 pr-5 text-left">
            <DialogTitle className="text-2xl font-semibold sm:text-3xl">
              Server info
            </DialogTitle>
            <DialogDescription className="text-lg font-semibold text-shades-0 dark:text-shades-0">
              {details?.eventName || 'Connection details for this event'}
            </DialogDescription>
            <p className="text-sm text-neutral-blue-300">
              Your joining details for this event.
            </p>
          </DialogHeader>
          {!details && !detailsError && (
            <p role="status">Loading server information…</p>
          )}
          {detailsError && (
            <div role="alert">
              <p>Could not load server information. Please try again.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setOpen(false)
                  window.setTimeout(() => setOpen(true), 0)
                }}
              >
                Retry
              </Button>
            </div>
          )}
          {details?.fields && (
            <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
              {rows
                .filter(([key]) => details.fields![key])
                .map(([key, label]) => (
                  <div
                    key={key}
                    className={
                      (key === 'name' && details.fields?.lobbyId) ||
                      key === 'lobbyId'
                        ? 'min-w-0'
                        : 'min-w-0 sm:col-span-2'
                    }
                  >
                    <p className="mb-1.5 text-sm text-neutral-blue-300">
                      {label}
                    </p>
                    <div className="flex min-h-11 items-center gap-1 rounded-md border border-neutral-blue-500 bg-neutral-blue-800 px-3 py-1">
                      <p className="min-w-0 flex-1 break-all text-sm">
                        {key === 'password' && !revealed
                          ? '••••••••'
                          : details.fields![key]}
                      </p>
                      {key === 'password' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0 gap-2 px-2"
                          aria-label={
                            revealed ? 'Hide password' : 'Reveal password'
                          }
                          onClick={() => setRevealed(!revealed)}
                        >
                          {revealed ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                          <span>{revealed ? 'Hide' : 'Show'}</span>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="square"
                        className="shrink-0"
                        aria-label={`Copy ${label.toLowerCase()}`}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              details.fields![key],
                            )
                            toast.success(`${label} copied`)
                          } catch {
                            toast.error(
                              'Copy failed. Please select and copy the text.',
                            )
                          }
                        }}
                      >
                        <Copy className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              {details.fields.instructions && (
                <div className="border-t border-neutral-blue-600 pt-5 sm:col-span-2">
                  <p className="mb-2 text-sm text-neutral-blue-300">
                    {preset.instructionsLabel}
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm text-neutral-blue-200">
                    {details.fields.instructions}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-row gap-3 sm:justify-end">
            {details?.fields?.joinUrl && (
              <Button
                asChild
                variant="primary"
                className="min-h-11 min-w-0 flex-1 gap-2 px-3"
              >
                <a
                  href={details.fields.joinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open join link <ExternalLink className="size-4 shrink-0" />
                </a>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="min-h-11 px-6 sm:min-w-32"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
